"use client"
import React, { useRef, useState } from 'react';
import toast from "react-hot-toast";
import { useCols, usePapers, useStore, useUserVip } from '@/stores/appStore';
import { Button } from '@/components/fun-ui/Button';
import { ExcelIcon, ExportIcon, HelpCircleIcon, VipPlusIcon, WordIcon } from '@/components/fun-ui/icons';
import { downloadBlobFile } from "@/lib/download";
import { Popover, PopoverTrigger, PopoverContent } from '@/components/fun-ui/Popover';
import { ExportPapersFileType, ExportPapersFormat } from '@/stores/slices/paperSlice';
import { Tip } from '@/components/fun-ui/Tip';
import { useUpgradeVip } from '@/components/payment/UpgradeVipButton';
import Image from 'next/image';
import { cn } from '@/lib/utils';

interface ExportPapersCSV extends React.HTMLAttributes<HTMLFormElement> {
  ids: string[];
  subjectId: string;
  iconOnly?: boolean;
}

type ExportingType = 'excel' | 'excelPlus' | 'word' | null;

export default function ExportPapersCSV({ ids, subjectId, iconOnly = false }: ExportPapersCSV) {
  const cols = useCols(subjectId);
  const papers = usePapers(subjectId);
  const exportPapers = useStore(state => state.exportPapers);
  const [exportingType, setExportingType] = useState<ExportingType>(null);
  const totalCount = papers?.length ?? 0;

  const handleDownloadCVS = async (props: {
    format: ExportPapersFormat,
    fileType: ExportPapersFileType,
  }) => {
    const {
      format,
      fileType = 'excel',
    } = props;
    const type: ExportingType = format === 'none' ? 'excel' : fileType === 'word' ? 'word' : 'excelPlus';
    setExportingType(type);
    toast.loading('导出中...');
    try {
      const data = await exportPapers({
        paperIds: ids,
        colKeys: cols.filter(col => col.selected).map(col => col.key.toString()),
        subjectId,
        format,
        fileType,
      });
      toast.dismiss();
      if (data) {
        downloadBlobFile(data.data, data.filename);
        toast.success('导出成功');
      } else {
        toast.error('导出失败');
      }
    } finally {
      setExportingType(null);
    }
  };

  const {
    vip
  } = useUserVip();

  const { upgradeVip } = useUpgradeVip();
  const [open, setOpen] = useState(false);

  const label = (ids?.length ? `导出 ${ids?.length} 行已选` : '导出全部');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div>
          <Tip label={iconOnly ? label : ""}>
            <div>
              <Button
                className={cn(
                  "flex items-center gap-1 whitespace-nowrap",
                  iconOnly ? "w-10 px-0" : "w-auto px-4"
                )}
                variant="outline"
              >
                <ExportIcon />
                <span className={cn(
                  iconOnly ? "hidden" : "inline-block"
                )}>
                  {
                    label
                  }
                </span>
              </Button>
            </div>
          </Tip>
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={4}
        className="p-0 m-0 w-fit"
      >
        <div className="flex flex-col p-2.5">
          <div
            className={cn(
              "flex flex-row items-center p-2.5 rounded-sm",
              exportingType === 'excel' ? "opacity-50 cursor-not-allowed" : "hover:bg-muted cursor-pointer"
            )}
            onClick={() => {
              if (exportingType === 'excel') return;
              handleDownloadCVS({
                format: 'none',
                fileType: 'excel',
              });
              setOpen(false);
            }}
          >
            <ExcelIcon />
            <span className="text-sm ml-3">Excel 表格(.xlsx)</span>
          </div>
          <div
            className={cn(
              "flex flex-row items-center p-2.5 rounded-sm",
              exportingType === 'excelPlus' ? "opacity-50 cursor-not-allowed" : "hover:bg-muted cursor-pointer"
            )}
            onClick={() => {
              if (exportingType === 'excelPlus') return;
              if (vip) {
                handleDownloadCVS({
                  format: 'plus',
                  fileType: 'excel',
                });
              } else {
                upgradeVip();
              }
              setOpen(false);
            }}
          >
            <ExcelIcon />
            <span className="text-sm ml-3">精装 Excel 表格(.xlsx)</span>
            <VipPlusIcon width={28} height={12} className="ml-2 shrink-0" aria-label="Plus" />
            <Tip
              side="bottom"
              sideOffset={20}
              label={
                <Image
                  src="https://oss.findinai.cn/storage/20250818/e18e93af963766237071151cfce3a7d3.png"
                  width={696}
                  height={402}
                  className="w-[464px] h-[268px]"
                  alt="精装 Excel 表格示例"
                />
              }
              className="ml-2 text-annotation-foreground"
              contentClassName="bg-transparent p-0 m-0 border-border shadow w-fit h-fit"
            >
              <HelpCircleIcon />
            </Tip>
          </div>
          <div
            className={cn(
              "flex flex-row items-center p-2.5 rounded-sm",
              exportingType === 'word' ? "opacity-50 cursor-not-allowed" : "hover:bg-muted cursor-pointer"
            )}
            onClick={() => {
              if (exportingType === 'word') return;
              if (vip) {
                // Word 导出限制：仅两种情况下提示——勾选为 0 且总数量 > 10；勾选数量 > 10
                const shouldPromptWordLimit =
                  (ids.length === 0 && totalCount > 10) || ids.length > 10;
                if (shouldPromptWordLimit) {
                  toast.error('Word 导出暂仅支持 10 篇以内文献');
                  setOpen(false);
                  return;
                }
                handleDownloadCVS({
                  format: 'plus',
                  fileType: 'word',
                });
              } else {
                upgradeVip();
              }
              setOpen(false);
            }}
          >
            <WordIcon />
            <span className="text-sm ml-3">Word 文档(.docx)</span>
            <VipPlusIcon width={28} height={12} className="ml-2 shrink-0" aria-label="Plus" />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );

}
